import React, { useEffect, useState, useRef, useCallback, useMemo, memo} from "react";
import {
  Dialog,
  DialogContent,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Backdrop,
  CircularProgress,
  Button,
  MenuItem,
  Popper,
  Grow,
  Paper,
  ClickAwayListener,
  MenuList,
} from "@mui/material";

import { Close, ArrowDropDown } from "@mui/icons-material";

import { getPayrollAuditLog, getEmployeeAudit } from "../api";

import { generateAuditExcel } from "../Utilities/AuditExcelGenerator";
import { generateAuditPdf } from "../Utilities/AuditPdfGenerator";

// --- Styles ---
const STYLES = {
  smallFont: { fontSize: "12px" },
  tableHead: {
    background: "linear-gradient(to bottom, #e6e6e6 0%, #f2f2f2 50%, #e6e6e6 100%) !important",
    borderTop: "1px solid #f2f2f2",
    borderBottom: "1px solid #f2f2f2",
    borderRight: "1px solid #f2f2f2",
    fontSize: 12,
    fontWeight: 500,
    padding: "4px 6px !important",
    height: 28,
    whiteSpace: "nowrap",
    position: "sticky",
    top: 0,
    zIndex: 130,
  },
  exportDropdown: {
    minWidth: 85,
    maxHeight: 200,
    overflowY: "auto",
    "& .MuiMenuItem-root": {
      fontSize: 11,
      lineHeight: "18px",
      paddingTop: "5px",
      paddingBottom: "5px",
      paddingLeft: "8px",
      paddingRight: "8px",
      borderBottom: "1px solid #d8d8d8",
    },
    "& .MuiMenuItem-root:last-child": { borderBottom: "none" },
    "& .Mui-selected": { backgroundColor: "#2f5d8a !important", color: "white" },
    "&::-webkit-scrollbar": { width: 6 },
    "&::-webkit-scrollbar-thumb": { backgroundColor: "#bdbdbd", borderRadius: 10 },
    "&::-webkit-scrollbar-thumb:hover": { backgroundColor: "#9e9e9e" },
  }
};

// --- Subcomponents ---
const AuditHeaderInfo = memo(({ headerInfo, auditType }) => (
  <Table size="small">
    <TableBody>
      <TableRow>
        <TableCell sx={{ fontWeight: 700, ...STYLES.smallFont }}>Corporation</TableCell>
        <TableCell sx={STYLES.smallFont}>{headerInfo?.corpName || "N/A"}</TableCell>

        {auditType === "Payroll" && (
          <>
            <TableCell sx={{ fontWeight: 700, ...STYLES.smallFont }}>Item Type</TableCell>
            <TableCell sx={STYLES.smallFont}>{headerInfo?.itemType || "N/A"}</TableCell>
          </>
        )}

        {auditType === "Employee" && (
          <>
            <TableCell sx={{ fontWeight: 700, ...STYLES.smallFont }}>Employee</TableCell>
            <TableCell sx={STYLES.smallFont}>{headerInfo?.empName || "N/A"}</TableCell>
          </>
        )}

        <TableCell sx={{ fontWeight: 700, ...STYLES.smallFont }}>Created Date</TableCell>
        <TableCell sx={STYLES.smallFont}>{headerInfo?.createdDate || "N/A"}</TableCell>

        <TableCell sx={{ fontWeight: 700, ...STYLES.smallFont }}>Created By</TableCell>
        <TableCell sx={STYLES.smallFont}>{headerInfo?.createdBy || "N/A"}</TableCell>
      </TableRow>
    </TableBody>
  </Table>
));

const PayrollAuditRow = memo(
  ({ audit, prevAudit, isWages, isEmployerTaxes }) => {
    const isChanged = useCallback(
      (key) => prevAudit && audit?.[key] !== prevAudit?.[key],
      [audit, prevAudit],
    );

    const itemNameContent = (
      <Box>
        Item Name:{" "}
        {isChanged("ItemName") ? <b>{audit.ItemName}</b> : audit.ItemName}
      </Box>
    );
    const payTypeContent = isWages && (
      <Box>
        Pay Type:{" "}
        {isChanged("PayType") ? <b>{audit.PayType}</b> : audit.PayType}
      </Box>
    );
    const wageTypeContent = isWages && (
      <Box>
        Wage Type:{" "}
        {isChanged("WageType") ? <b>{audit.WageType}</b> : audit.WageType}
      </Box>
    );

    const configContent = (audit?.PayrollAuditDeptJobDetails || []).map(
      (d, j) => {
        const prevDetail = prevAudit?.PayrollAuditDeptJobDetails?.find(
          (p) => p.Department === d.Department && p.JobTitle === d.JobTitle,
        );
        const isNew = prevAudit && !prevDetail;
        const isDetailChanged = (key) => prevDetail && d[key] !== prevDetail[key];

        return (
          <Box key={j} sx={{ mb: 1, "& b": { fontWeight: "bold" } }}>
            Department: {isNew ? <b>{d.Department}</b> : d.Department} <br />
            Job Title: {isNew ? <b>{d.JobTitle}</b> : d.JobTitle} <br />
            Expense COA:{" "}
            {isNew || isDetailChanged("AccountName") ? (
              <b>{d.AccountName}</b>
            ) : (
              d.AccountName
            )}{" "}
            <br />
            {isEmployerTaxes && (
              <>
                Payable COA:{" "}
                {isNew || isDetailChanged("LiabilityAccName") ? (
                  <b>{d.LiabilityAccName}</b>
                ) : (
                  d.LiabilityAccName
                )}
              </>
            )}
          </Box>
        );
      },
    );

    return (
      <>
        <TableCell sx={{ verticalAlign: "middle", textAlign: "left" }}>
          {itemNameContent}
          {payTypeContent}
          {wageTypeContent}
        </TableCell>
        <TableCell sx={{ verticalAlign: "middle", textAlign: "left" }}>
          {configContent}
        </TableCell>
      </>
    );
  },
);

const EmployeeAuditRow = memo(({ audit, prevAudit }) => {
  const isChanged = useCallback((key) => prevAudit && audit?.[key] !== prevAudit?.[key], [audit, prevAudit]);

  const uniquePayroll = useMemo(() => {
    const payrollDetails = Array.isArray(audit?.PayrollDetails) ? audit.PayrollDetails.filter((p) => p?.Department || p?.JobTitle) : [];
    return [...new Map(payrollDetails.map((item) => [`${item.Department}-${item.JobTitle}`, item])).values()];
  }, [audit?.PayrollDetails]);

  return (
    <>
      <TableCell sx={{ verticalAlign: "middle", textAlign: "left" }}>
        Name: {isChanged("EmployeeName") ? <b>{audit?.EmployeeName}</b> : audit?.EmployeeName} <br />
        Phone: {isChanged("MobileNum") ? <b>{audit?.MobileNum}</b> : audit?.MobileNum} <br />
        Email: {isChanged("EmailID") ? <b>{audit?.EmailID}</b> : audit?.EmailID} <br />
        SSN: {isChanged("Ssn") ? <b>{audit?.Ssn}</b> : audit?.Ssn} <br />
        Check To Be Printed as: {isChanged("CheckToBePrinted") ? <b>{audit?.CheckToBePrinted}</b> : audit?.CheckToBePrinted}
      </TableCell>
      <TableCell sx={{ verticalAlign: "middle", textAlign: "left" }}>
        {uniquePayroll.map((p, j) => {
          const prevDetails = Array.isArray(prevAudit?.PayrollDetails) ? prevAudit.PayrollDetails : [];
          const prevPay = prevDetails.find((x) => x?.Department === p?.Department && x?.JobTitle === p?.JobTitle);
          const isNew = prevAudit && !prevPay;
          const freqChanged = prevPay && p?.Frequency !== prevPay?.Frequency;

          return (
            <Box key={j} sx={{ mb: 1 }}>
              Pay Frequency: {freqChanged || isNew ? <b>{p?.Frequency}</b> : p?.Frequency} <br />
              Department: {isNew ? <b>{p?.Department}</b> : p?.Department} <br />
              Job Title: {isNew ? <b>{p?.JobTitle}</b> : p?.JobTitle}
            </Box>
          );
        })}
      </TableCell>
    </>
  );
});

const AuditLog = ({ open, onClose, auditType, id }) => {
  const [headerInfo, setHeaderInfo] = useState({});
  const [auditRows, setAuditRows] = useState([]);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const exportAnchorRef = useRef(null);

  const isEmployerTaxes = [
    "EmployerTaxes",
    "employer_taxes",
    "Employer Taxes",
  ].includes(headerInfo?.itemType);
  const isWages = ["Wages", "wages"].includes(headerInfo?.itemType);

  useEffect(() => {
    if (!open || !id) return;

    const loadAudit = async () => {
      setFetchLoading(true);

      try {
        let data = null;

        if (auditType === "Payroll") {
          const rawData = await getPayrollAuditLog(id);
          data = rawData || {};
          setHeaderInfo({
            corpName: data?.CorpName,
            itemType: data?.ItemType,
            createdDate: data?.CreatedDate,
            createdBy: data?.CreatedBy,
          });
          setAuditRows(Array.isArray(data?.PayrollAuditTable) ? data.PayrollAuditTable : []);
        } 
        else if (auditType === "Employee") {
          const rawData = await getEmployeeAudit(id);
          data = rawData || {};
          setHeaderInfo({
            corpName: data?.CorpName,
            empName: data?.EmpName,
            createdDate: data?.CreatedDate,
            createdBy: data?.CreatedBy,
          });
          setAuditRows(Array.isArray(data?.EmployeeAuditGrid) ? data.EmployeeAuditGrid : []);
        }
      } catch (err) {
        console.error("Audit Load Error:", err);
      } finally {
        setFetchLoading(false);
      }
    };

    loadAudit();
  }, [open, id, auditType]);

  const handleExportToggle = useCallback(() => {
    setExportOpen((prevOpen) => !prevOpen);
  }, []);

  const handleExportClose = useCallback((event) => {
    if (exportAnchorRef.current && exportAnchorRef.current.contains(event.target)) {
      return;
    }
    setExportOpen(false);
  }, []);

  const handlePdfExport = useCallback((e) => {
    generateAuditPdf(auditRows, headerInfo, auditType);
    handleExportClose(e);
  }, [auditRows, headerInfo, auditType, handleExportClose]);

  const handleExcelExport = useCallback((e) => {
    generateAuditExcel(auditRows, headerInfo, auditType);
    handleExportClose(e);
  }, [auditRows, headerInfo, auditType, handleExportClose]);

  return (
    <Dialog open={open} onClose={onClose} fullScreen sx={{ zIndex: 99999 }}>
      <Backdrop open={fetchLoading} sx={{ zIndex: 100000, color: "#fff" }}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <Toolbar
        sx={{
          backgroundColor: "#F8F9FA",
          minHeight: "40px !important",
          height: "40px",
          px: 1,
        }}
      >
        <Typography sx={{ flex: 1, color: "#2782EC" }} fontWeight={600}>
          {auditType} Audit Log
        </Typography>

        <Button
          ref={exportAnchorRef}
          size="small"
          variant="outlined"
          onClick={handleExportToggle}
          endIcon={<ArrowDropDown />}
          sx={{ height: 26, fontSize: 11 }}
        >
          Export
        </Button>

        <Popper
          sx={{ zIndex: 100000 }}
          open={exportOpen}
          anchorEl={exportAnchorRef.current}
          role={undefined}
          transition
          disablePortal
          placement="bottom-end"
        >
          {({ TransitionProps, placement }) => (
            <Grow
              {...TransitionProps}
              style={{
                transformOrigin:
                  placement === "bottom-end" ? "right top" : "right bottom",
              }}
            >
              <Paper sx={STYLES.exportDropdown}>
                <ClickAwayListener onClickAway={handleExportClose}>
                  <MenuList autoFocusItem={exportOpen} id="export-menu">
                    <MenuItem onClick={handlePdfExport}>PDF</MenuItem>
                    <MenuItem onClick={handleExcelExport}>Excel</MenuItem>
                  </MenuList>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>

        <IconButton
          size="small"
          edge="start"
          onClick={onClose}
          style={{
            marginLeft: 12,
            backgroundColor: "#686868",
            borderRadius: 2,
            width: 20,
            height: 20,
          }}
          disableRipple
        >
          <Close sx={{ color: "white", fontSize: "11px" }} />
        </IconButton>
      </Toolbar>

      <DialogContent>
        <AuditHeaderInfo headerInfo={headerInfo} auditType={auditType} />
        
        <TableContainer sx={{ mt: 2 }}>
          <Table
            size="small"
            sx={{
              border: "1px solid #d5d5d5",
              "& td, & th": {
                border: "1px solid #d5d5d5",
                padding: "4px 6px",
                ...STYLES.smallFont,
              },
            }}
          >
            <TableHead>
              <TableRow>
                {auditType === "Payroll" ? (
                  <>
                    <TableCell sx={STYLES.tableHead}>Item Name</TableCell>
                    <TableCell sx={STYLES.tableHead}>Configuration</TableCell>
                  </>
                ) : (
                  <>
                    <TableCell sx={STYLES.tableHead}>Employee Information</TableCell>
                    <TableCell sx={STYLES.tableHead}>Payroll Information</TableCell>
                  </>
                )}
                <TableCell sx={STYLES.tableHead}>Action</TableCell>
                <TableCell sx={STYLES.tableHead}>Action Date</TableCell>
                <TableCell sx={STYLES.tableHead}>Action By</TableCell>
                <TableCell sx={STYLES.tableHead}>Status</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {Array.isArray(auditRows) && auditRows.length > 0 ? (
                auditRows.map((audit, i) => {
                  const prev = auditRows[i + 1];

                  return (
                    <TableRow key={i}>
                      {auditType === "Payroll" ? (
                        <PayrollAuditRow audit={audit} prevAudit={prev} isWages={isWages} isEmployerTaxes={isEmployerTaxes} />
                      ) : (
                        <EmployeeAuditRow audit={audit} prevAudit={prev} />
                      )}

                      <TableCell align="center" sx={{ verticalAlign: "middle" }}>
                        {audit?.ActionName}
                      </TableCell>
                      <TableCell align="center" sx={{ verticalAlign: "middle" }}>
                        {audit?.ActionDate}
                      </TableCell>
                      <TableCell align="center" sx={{ verticalAlign: "middle" }}>
                        {prev && audit?.ActionByName !== prev?.ActionByName ? <b>{audit?.ActionByName}</b> : audit?.ActionByName}
                      </TableCell>
                      <TableCell align="center" sx={{ verticalAlign: "middle" }}>
                        {prev && audit?.IsActive !== prev?.IsActive ? <b>{audit?.IsActive}</b> : audit?.IsActive}
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                    No audit records found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
    </Dialog>
  );
};

export default AuditLog;
